import React from "react";
import { useTranslation } from "react-i18next";

import { SpinnerFullScreen } from "../../../components/SpinnerFullScreen";
import { DashboardTodayResponseDto } from "../../../model/dashboard/dto/DashboardTodayResponseDto";
import { dashboardSliceSelectors } from "../../../redux/dashboard/dashboard.selector";
import { useAppSelector } from "../../../redux/store";
import { TestIDs } from "../../../tests/types/testIds";
import { EntityIcons } from "../../../utils/GlobalIcons";
import {
  DashboardEmpty,
  DashboardList,
  DashboardRowTitle,
} from "./DashboardListRow";
import { DashboardPanel } from "./DashboardPanel";
import { DashboardTaskRow } from "./DashboardTaskRow";

type DashboardTasksDueProps = {
  today: DashboardTodayResponseDto | null;
};

export const DashboardTasksDue: React.FC<DashboardTasksDueProps> = ({
  today,
}) => {
  const { t } = useTranslation();
  const failed = useAppSelector(dashboardSliceSelectors.selectTodayFailed);

  const hasTasks = Boolean(
    today?.overdueTasks.length || today?.dueTodayTasks.length,
  );
  const hiddenOverdue = today
    ? today.tasks.overdue - today.overdueTasks.length
    : 0;

  const content = () => {
    if (!today) {
      return failed ? (
        <DashboardEmpty>{t("dashboard.today.error")}</DashboardEmpty>
      ) : (
        <SpinnerFullScreen containerHeight="9rem" />
      );
    }

    if (!hasTasks) {
      return <DashboardEmpty>{t("dashboard.tasks_due.empty")}</DashboardEmpty>;
    }

    return (
      <>
        <DashboardList>
          {today.overdueTasks.map((task) => (
            <DashboardTaskRow key={task._id} task={task} overdue />
          ))}
          {today.dueTodayTasks.map((task) => (
            <DashboardTaskRow key={task._id} task={task} overdue={false} />
          ))}
        </DashboardList>
        {hiddenOverdue > 0 ? (
          <DashboardRowTitle to="/tasks">
            {t("dashboard.today.attention.more_overdue", {
              count: hiddenOverdue,
            })}
          </DashboardRowTitle>
        ) : null}
      </>
    );
  };

  return (
    <DashboardPanel
      title={t("dashboard.tasks_due.title")}
      cta={{
        to: "/tasks",
        label: t("dashboard.widget.tasks.btn"),
        icon: EntityIcons.Tasks,
      }}
      dataTestId={TestIDs.dashboard.tasksDue}
    >
      {content()}
    </DashboardPanel>
  );
};
